/**
 * Automatic sign-out after inactivity, a HIPAA safeguard for shared clinic
 * computers and family phones. After 14 minutes without a key, click, touch or
 * scroll a notice offers one minute to stay; at 15 the session ends.
 *
 * Activity in any open tab counts, through localStorage, so a clinician working
 * in a second tab is not signed out of the first.
 */

import { useEffect, useRef, useState } from 'react';

export const IDLE_MINUTES = 15;
const WARN_SECONDS = 60;
const STORAGE_KEY = 'mcos-last-activity';
const EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

function readLast(): number {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || Date.now();
  } catch {
    return Date.now();
  }
}

function writeLast(at: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(at));
  } catch {
    // Storage blocked: this tab's own timer still works.
  }
}

export function IdleSignOut({ onSignOut, minutes = IDLE_MINUTES }: { onSignOut: () => void; minutes?: number }) {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const last = useRef(Date.now());
  const signedOut = useRef(false);

  useEffect(() => {
    last.current = Date.now();
    writeLast(last.current);
    let throttle = 0;
    const touch = () => {
      const now = Date.now();
      last.current = now;
      if (now - throttle > 5000) {
        throttle = now;
        writeLast(now);
      }
    };
    for (const name of EVENTS) window.addEventListener(name, touch, { passive: true });
    const tick = window.setInterval(() => {
      const latest = Math.max(last.current, readLast());
      const left = Math.round((latest + minutes * 60_000 - Date.now()) / 1000);
      if (left <= 0 && !signedOut.current) {
        signedOut.current = true;
        onSignOut();
      } else if (left <= WARN_SECONDS) {
        setSecondsLeft(left);
      } else {
        setSecondsLeft(null);
      }
    }, 1000);
    return () => {
      for (const name of EVENTS) window.removeEventListener(name, touch);
      window.clearInterval(tick);
    };
  }, [minutes, onSignOut]);

  if (secondsLeft === null) return null;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-[rgba(20,23,28,0.45)] px-4" role="alertdialog" aria-modal="true" aria-label="Still there?">
      <div className="w-full max-w-sm rounded-[14px] bg-white p-5 text-center shadow-[0_16px_48px_rgba(20,23,28,0.25)]" data-testid="idle-warning">
        <p className="font-display text-[20px] text-navy">Still there?</p>
        <p className="mt-1 text-[14px] text-[#4d6072]">For privacy you will be signed out in {Math.max(secondsLeft, 0)} seconds.</p>
        <button
          type="button"
          autoFocus
          className="mt-4 h-11 w-full rounded-[10px] bg-forest text-[15px] font-semibold text-white hover:bg-forest-deep"
          onClick={() => {
            last.current = Date.now();
            writeLast(last.current);
            setSecondsLeft(null);
          }}
        >
          Stay signed in
        </button>
      </div>
    </div>
  );
}
